<template>
	<v-form
		class="register-form"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="validateAndSubmit"
	>
		<v-row>
			<v-col cols="12">
				<p class="register-form__description mb-0">Email</p>
				<v-text-field
					class="pt-0"
					color="red"
					v-model="fields.email"
					:error-messages="field_errors.email"
					:rules="rules.email"
					required
					placeholder="Enter your email"
				>
				</v-text-field>
			</v-col>
			<v-col cols="12" class="pb-0">
				<p class="register-form__description mb-0">Password</p>
				<v-text-field
					class="pt-0 password-field"
					:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
					:type="showPassword ? 'text' : 'password'"
					v-model="fields.password"
					required
					:rules="rules.password"
					:error-messages="field_errors.password"
					placeholder="Enter your password"
					@click:append="showPassword = !showPassword"
				>
				</v-text-field>
			</v-col>
			<v-col cols="12" class="pb-0">
				<p class="register-form__description mb-0">Password Confirmation</p>
				<v-text-field
					class="pt-0 password-field"
					:append-icon="showRePassword ? 'mdi-eye' : 'mdi-eye-off'"
					:type="showRePassword ? 'text' : 'password'"
					v-model="fields.rePassword"
					required
					:rules="rules.password.concat(passwordConfirmationRule)"
					:error-messages="field_errors.rePassword"
					placeholder="Enter your password"
					@click:append="showRePassword = !showRePassword"
				>
				</v-text-field>
			</v-col>
			<v-col cols="12">
				<v-row no-gutters>
					<v-spacer />
					<a
						class="text-right register-form__link register-form__link-signup"
						@click="navigateToLoginPage"
						>Sign In</a
					>
				</v-row>
			</v-col>
			<p v-for="error in errors" :key="error" class="text-center red--text">
				{{ error }}
			</p>
			<p
				class="text-center red--text"
				v-for="error in this.authErrors"
				:key="error"
				style="width: 100%"
			>
				{{ error }}
			</p>
			<v-col cols="12" class="text-center mb-4">
				<v-btn
					class="align-center px-11 py-6 register-form__submit"
					type="submit"
					:loading="loading"
				>
					Sign up
				</v-btn>
			</v-col>
			<v-col cols="12" class="text-center register-form__social pb-0">
				<p class="mb-5">Sign in with social networks</p>
				<v-btn color="white mr-8">
					<font-awesome-icon
						size="2x"
						color="#202232"
						:icon="['fab', 'google']"
						class="icon alt"
					/>
				</v-btn>
				<v-btn color="white mr-8">
					<font-awesome-icon
						size="2x"
						color="#202232"
						:icon="['fab', 'facebook-f']"
						class="icon alt"
					/>
				</v-btn>
				<v-btn color="white">
					<font-awesome-icon
						size="2x"
						color="#202232"
						:icon="['fab', 'apple']"
						class="icon alt"
					/>
				</v-btn>
			</v-col>
		</v-row>
	</v-form>
</template>

<script>
import Form from '../../mixins/Form'
import { mapActions, mapGetters } from 'vuex'
import { ruleEmail, rulePassword, ruleRequired } from '@/utils/rules'

export default {
	name: 'RegisterForm',
	mixins: [Form],
	data: () => ({
		fields: {
			email: '',
			password: '',
			rePassword: '',
		},
		showPassword: false,
		showRePassword: false,
	}),
	computed: {
		...mapGetters('auth', ['authErrors']),
		rules() {
			return {
				email: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => ruleEmail(v) || 'Please enter a valid email address.',
				],
				password: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => rulePassword(v) || 'Password must contain a minimum of 8 characters.',
				],
				rePassword: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => rulePassword(v) || 'Password must contain a minimum of 8 characters.',
				],
			}
		},
		passwordConfirmationRule() {
			return () => this.fields.password === this.fields.rePassword || 'Password must match'
		},
	},
	methods: {
		...mapActions('auth', ['register']),
		submitForm() {
			const { email, password, rePassword } = this.fields
			return this.register({ email, password, password_confirmation: rePassword })
		},
		navigateToLoginPage() {
			this.$router.push({ name: 'login' })
		},
	},
}
</script>

<style lang="scss">
.register-form {
	font-family: Montserrat, system-ui;

	.theme--light.v-input input {
		color: #000;
	}

	.v-text-field {
		input {
			padding: 10px 0 20px;
			font-size: 20px;
			font-weight: 500;
			max-height: 50px;
		}
	}

	.theme--light.v-text-field > .v-input__control > .v-input__slot:before {
		color: #e5e5e5;
		border-color: #e5e5e5;
	}

	.password-field {
		.v-input__append-inner {
			margin-top: 0;
			.v-input__icon--append {
				.v-icon {
					color: #e5e5e5;
					padding: 30px 0 20px;
				}
			}
		}
	}

	input::placeholder {
		color: #e5e5e5 !important;
		opacity: 1;
	}

	&__description {
		font-weight: bold;
		font-size: 18px;
	}

	&__link {
		font-style: normal;
		font-weight: 500;
		font-size: 15px;

		&-signup {
			font-weight: 700;
		}
	}

	&__social {
		p {
			font-weight: 500;
			font-size: 14px;
			line-height: 17px;
		}

		button {
			width: 61px;
			height: 61px !important;
			box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
			border-radius: 12px;
		}
	}

	button[type='submit'] {
		border-radius: 54.5px;
		font-style: normal;
		font-weight: bold;
		font-size: 16px;
		text-transform: none;
		background-color: var(--accent-color) !important;
		color: #fff;
	}
}
</style>
